/**
 * offices_wave.ts — OFFICES BY TIER (society wave, 2026-09-19), pure.
 *
 * Government shrinks with the place. A CITY keeps a full council, a TOWN a
 * small one, a HAMLET a moot with a reeve and a hayward, and a CAPITAL adds one
 * elected ALDERMAN per district (districts are wards of their capital — they
 * have no mayor of their own; London's 24 wards each returned an alderman).
 *
 * `settlement_office.office` is a string column, so every id here is just a new
 * value. Offices are STICKY (the guildmaster rule): a sitting holder keeps the
 * seat until they are gone; the sweep only fills vacancies.
 *
 * MIRROR: client/src/lib/offices_wave.ts must stay byte-identical (the docket
 * and the Metrics tab label offices from it).
 */

export const ALDERMAN_PREFIX = 'alderman:';
export const isAldermanOffice = (office: string): boolean => office.startsWith(ALDERMAN_PREFIX);
export const aldermanOffice = (ward: number): string => `${ALDERMAN_PREFIX}${String(ward).padStart(2, '0')}`;
export const aldermanWard = (office: string): number => Number(office.slice(ALDERMAN_PREFIX.length)) || 0;

export const OFFICE_LABELS: Record<string, string> = {
  // pre-wave
  keeper: 'keeper of the rites', guildmaster: 'guildmaster of the works', regent: 'regent of the town',
  // hamlet
  reeve: 'reeve of the hamlet', hayward: 'hayward of the fields',
  // town
  constable: 'constable of the watch', warden_clerk: 'market warden and clerk',
  // city
  treasurer: 'treasurer of the city', captain: 'captain of the watch', magistrate: 'magistrate',
  market_warden: 'warden of the market', works_master: 'master of works', clerk: 'clerk of the city',
  warehouse_keeper: 'keeper of the warehouse', landing_master: 'master of the landing',
};

/** Human label of an office id ("alderman of the 7th ward"). */
export function officeLabel(office: string): string {
  if (isAldermanOffice(office)) {
    const n = aldermanWard(office) + 1;
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
    return `alderman of the ${n}${suffix} ward`;
  }
  return OFFICE_LABELS[office] ?? office.replace(/_/g, ' ');
}

const HAMLET_OFFICES = ['reeve', 'hayward'];
const TOWN_OFFICES = ['constable', 'warden_clerk'];
const CITY_OFFICES = ['treasurer', 'captain', 'magistrate', 'market_warden', 'works_master', 'clerk', 'warehouse_keeper'];

/** The wave offices a settlement of this tier fills (the pre-wave keeper,
 *  guildmaster and regent keep their own appointment paths). */
export function officesForTier(tier: string, districts: number): string[] {
  switch (tier) {
    case 'hamlet': return [...HAMLET_OFFICES];
    case 'town': case 'circuit': return [...TOWN_OFFICES];
    case 'city': return [...CITY_OFFICES];
    case 'capital': return [...CITY_OFFICES, 'landing_master', ...Array.from({ length: districts }, (_, i) => aldermanOffice(i))];
    default: return [];
  }
}

export interface OfficeCandidate {
  id: bigint; job: string; boldness: number; greed: number; diligence: number; sociability: number;
  /** Total npc_skill levels (a tradesperson's seniority). */
  skill: number;
  /** A post key (`svc:<post>`) the candidate holds, or ''. */
  post: string;
}

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
const h01 = (...parts: Array<string | number | bigint>): number => fnv(parts.join('|')) / 0xffffffff;

/** How well a resident suits an office (higher wins; −1 = ineligible). */
export function officeScore(office: string, c: OfficeCandidate): number {
  const tie = h01(c.id, 'office', office) * 3;
  if (isAldermanOffice(office)) return c.sociability * 1.2 + c.diligence * 0.5 + tie;
  switch (office) {
    case 'hayward': return (['farmer', 'fisher', 'forester', 'tapper', 'herbalist'].includes(c.job) ? 40 : 0) + c.diligence + tie;
    case 'constable': case 'captain': return (c.job === 'guard' || c.post === 'svc:watch' ? 50 : c.job === 'hunter' || c.job === 'scout' ? 20 : -1000) + c.boldness * 0.7 + c.diligence * 0.5 + tie;
    case 'warden_clerk': case 'market_warden': return (c.job === 'merchant' ? 25 : 0) + c.diligence + (100 - c.greed) * 0.6 + tie;
    case 'treasurer': return (100 - c.greed) * 1.1 + c.diligence * 0.8 + tie;
    case 'magistrate': return (c.post === 'svc:magistrate' ? 40 : 0) + (100 - c.boldness) * 0.7 + c.diligence * 0.9 + tie;
    case 'works_master': return (c.job === 'smith' || c.post === 'svc:builder' ? 30 : 0) + c.diligence * 1.1 + c.skill * 4 + tie;
    case 'clerk': return (c.post === 'svc:scribe' ? 45 : 0) + c.diligence + tie;
    case 'warehouse_keeper': return (c.post === 'svc:warehouse' ? 60 : c.job === 'merchant' ? 20 : 0) + c.diligence + tie;
    case 'landing_master': return (c.post === 'svc:landing_master' ? 60 : 0) + c.diligence + c.boldness * 0.3 + tie;
    default: return c.sociability + c.diligence + tie;
  }
}

/** Offices a settlement may fill in ONE sweep (appointments are quiet, but a
 *  capital filling 38 seats at once would still be a row storm). */
export function appointmentsPerSweep(tier: string): number {
  return tier === 'capital' ? 4 : 1;
}
